import {ENS_ALERT_TYPES, EnsAlertType, EnsLevelInputDto} from "../../../store/features/ens/types";
import {TYPE_DOT_COLOR, TYPE_LABEL} from "../utils";
import {formatDuration} from "./utils";

const TYPE_CHIP: Record<EnsAlertType, string> = {
    FRAUD: 'bg-[#fff7ed] text-[#b45309]',
    TASK: 'bg-[#f0fdf4] text-[#15803d]',
    POLICY: 'bg-[#eff6ff] text-[#1d4ed8]',
    CUSTOM: 'bg-[#faf5ff] text-[#6d28d9]',
};

const DESCRIPTIONS: Record<EnsAlertType, string> = {
    FRAUD: 'Time-critical security incident. Aggressive escalation cadence (seconds to minutes).',
    TASK: 'Operational task acknowledgement. Slow escalation cadence over hours.',
    POLICY: 'Policy or compliance update. Mid-paced cadence over hours.',
    CUSTOM: 'Custom alert with default escalation, adjustable in the next step.',
};

interface Props {
    type: EnsAlertType | null;
    presetByType: Map<EnsAlertType, EnsLevelInputDto[]>;
    onChange: (type: EnsAlertType) => void;
}

const buildPreview = (levels?: EnsLevelInputDto[]): string[] => {
    if (!levels || levels.length < 2) return [];
    const transitions: string[] = [];
    for (let i = 1; i < levels.length; i++) {
        transitions.push(`L${i}->L${i + 1}: ${formatDuration(levels[i].delaySecondsFromPrevious)}`);
    }
    return transitions;
};

export const Step1Type = ({type, presetByType, onChange}: Props) => (
    <div className="flex min-w-0 flex-col gap-5">
        <div>
            <h3 className="text-sm font-semibold text-ink">Alert type</h3>
            <p className="mt-1 text-sm leading-5 text-ink-muted">
                The type sets a default escalation plan. You can adjust delays in step 3.
            </p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {ENS_ALERT_TYPES.map((option) => {
                const active = type === option;
                const transitions = buildPreview(presetByType.get(option));
                return (
                    <button
                        key={option}
                        type="button"
                        aria-pressed={active}
                        onClick={() => onChange(option)}
                        className={`box-border flex min-h-[148px] min-w-0 cursor-pointer flex-col gap-3 rounded-xl border-2 border-solid p-5 text-left transition-colors hover:border-brand-focus hover:bg-brand-tint-subtle focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-focus ${
                            active ? 'border-brand bg-brand-tint shadow-sm' : 'border-line bg-surface'
                        }`}
                    >
                        <div className="flex min-w-0 items-center gap-2">
                            <span className={`h-2 w-2 shrink-0 rounded-full ${TYPE_DOT_COLOR[option]}`}/>
                            <span className="truncate text-sm font-semibold text-ink">{TYPE_LABEL[option]}</span>
                        </div>
                        <div className="text-xs leading-5 text-ink-muted">{DESCRIPTIONS[option]}</div>
                        <div className="mt-auto flex flex-wrap gap-1.5 pt-1">
                            {transitions.length > 0 ? (
                                transitions.map((transition) => (
                                    <span
                                        key={transition}
                                        className={`max-w-full break-words rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${TYPE_CHIP[option]}`}
                                    >
                                        {transition}
                                    </span>
                                ))
                            ) : (
                                <span className="rounded-md bg-zinc-100 px-1.5 py-0.5 text-[11px] font-semibold text-ink-muted">
                                    {option === 'CUSTOM' ? 'Custom escalation' : 'Set in next step'}
                                </span>
                            )}
                        </div>
                    </button>
                );
            })}
        </div>
    </div>
);
